// MAGAZINE design kit — the shared component vocabulary every screen is built
// from. Magazine is now MediaTracker's single design language (the old calm /
// fantasy skins are dropped), so these helpers are used app-wide rather than
// gated behind getSkin(). Spec: design_documents/Magazinestyle/
// Magazine-Rollout-AllScreens.md §1.
//
// Everything is rendered with INLINE styles (the convention the original
// magazine Home/gallery established) so a screen conversion only ever touches
// its own view file plus this kit — no shared CSS churn. The handful of things
// that genuinely need global CSS (shimmer keyframes, the body colour-bloom,
// :focus-visible ring) live in main.css.
import { el } from "../dom.js";
import {
	MAG,
	MAG_CAT,
	MAG_CAT_ORDER,
	magCat,
	coverOf,
	yearOf,
	magTileBg,
	magSticker,
	magStickerAbsolute,
	magSectionHead,
} from "../../utils/magazine.js";

// Re-export the lower-level primitives so screens can import the whole kit from
// one place.
export {
	MAG,
	MAG_CAT,
	MAG_CAT_ORDER,
	magCat,
	coverOf,
	yearOf,
	magTileBg,
	magSticker,
	magStickerAbsolute,
};

// A rotating palette of poppy accent colours for section headers / stickers, so
// successive sections don't all read the same hue.
export const MAG_ACCENTS = ["#b6f23d", "#ffd60a", "#19d3e8", "#ff3d8b", "#8b3dff", "#ff7a1a"];

// ---- 1a. Kicker / section header ------------------------------------------

// A skewed sticker label. Dark text on light (lime/yellow/cyan) fills, white on
// dark fills — the caller passes the right `color` when the fill is dark.
export function magKicker(text, color = MAG.lime, { size = "10px", textColor } = {}) {
	return magSticker(text, { bg: color, color: textColor ?? "#0b0b0f", size });
}

// Kicker + a fading 2px rule — the standard section divider. (magSectionHead is
// the original implementation; re-exported here under the doc's name.)
export const magSectionHeader = magSectionHead;

// ---- 1b. Frosted glass -----------------------------------------------------

// The signature surface. `variant: "panel"` (default, on plain backgrounds) or
// `"over-art"` (heavier, for bars sitting on cover art). Extra style overrides
// merge last.
export function magGlass(children = [], { variant = "panel", style = {}, role, onClick, tag = "div" } = {}) {
	const base = variant === "over-art"
		? {
			background: "rgba(14,12,20,.42)",
			backdropFilter: "blur(18px) saturate(1.5)",
			webkitBackdropFilter: "blur(18px) saturate(1.5)",
			border: `1px solid ${MAG.line}`,
			borderRadius: "14px",
			boxShadow: "0 8px 28px rgba(0,0,0,.4), inset 0 1px 0 rgba(255,255,255,.14)",
		}
		: {
			background: MAG.bgGlass,
			backdropFilter: "blur(14px) saturate(1.4)",
			webkitBackdropFilter: "blur(14px) saturate(1.4)",
			border: `1px solid ${MAG.line}`,
			borderRadius: "14px",
		};
	const props = { style: { ...base, ...style } };
	if (role) props.role = role;
	if (onClick) {
		props.onclick = onClick;
		props.style.cursor = "pointer";
	}
	const kids = Array.isArray(children) ? children : [children];
	return el(tag, props, ...kids.filter((c) => c != null));
}

// ---- 1c. Cover tile --------------------------------------------------------

// A textured colour-field cover tile (real art when present, else the
// category-tinted halftone duotone). `children` are positioned absolutely
// inside (stickers, titles). Pass height/width as CSS strings.
export function magCover(item, { height = "160px", width, radius = "14px", onClick, children = [], scrim = false, style = {} } = {}) {
	const kids = [...(Array.isArray(children) ? children : [children])];
	if (scrim) {
		kids.unshift(
			el("div", {
				style: {
					position: "absolute", inset: "0",
					background: "linear-gradient(transparent 40%, rgba(0,0,0,.55))",
					pointerEvents: "none",
				},
			}),
		);
	}
	const props = {
		style: {
			position: "relative", height, borderRadius: radius, overflow: "hidden",
			boxShadow: "0 8px 22px rgba(0,0,0,.45)", background: magTileBg(item),
			...(width ? { width } : {}),
			...style,
		},
	};
	if (onClick) {
		props.role = "button";
		props.onclick = onClick;
		props.style.cursor = "pointer";
	}
	return el("div", props, ...kids.filter(Boolean));
}

// ---- 1d. Feature hero ------------------------------------------------------

// Full-bleed feature header for detail + series/season screens. `item` drives
// the textured background; `kicker` is the small cyan line above the title;
// `bar` is the glass stat/action bar pinned to the bottom (build with magGlass).
export function magFeatureHero(item, {
	height = "320px",
	kicker,
	title,
	onBack,
	backLabel = "‹",
	kebab, // optional element for the top-right (e.g. a menu button)
	sticker, // optional { text, color } status sticker
	bar, // optional element pinned to the bottom (a glass bar)
} = {}) {
	const children = [];

	if (onBack) {
		children.push(
			el("button", {
				type: "button",
				onclick: onBack,
				"aria-label": "Back",
				style: {
					position: "absolute", top: "14px", left: "14px", height: "40px", width: "40px",
					border: `1px solid ${MAG.line}`, background: "rgba(10,10,14,.45)", borderRadius: "12px",
					color: "#fff", display: "flex", alignItems: "center", justifyContent: "center",
					cursor: "pointer", fontSize: "24px", lineHeight: "1", paddingBottom: "3px",
					backdropFilter: "blur(10px)", webkitBackdropFilter: "blur(10px)",
				},
			}, backLabel),
		);
	}
	if (kebab) {
		children.push(
			el("div", { style: { position: "absolute", top: "14px", right: "14px", display: "flex", gap: "8px" } }, kebab),
		);
	}
	if (sticker) {
		children.push(
			el("div", {
				style: {
					position: "absolute", top: "62px", left: "14px",
					fontFamily: "'Archivo', sans-serif", fontWeight: "900", fontSize: "11px",
					letterSpacing: ".08em", textTransform: "uppercase", color: sticker.textColor ?? "#0b0b0f",
					background: sticker.color ?? MAG.lime, padding: "5px 10px",
					transform: "skewX(-7deg) rotate(-2deg)", boxShadow: "0 4px 14px rgba(0,0,0,.35)",
				},
			}, sticker.text),
		);
	}

	const titleBlock = el(
		"div",
		{ style: { position: "absolute", left: "16px", right: "16px", bottom: bar ? "92px" : "16px" } },
		kicker
			? el("div", {
				style: {
					fontFamily: "'Archivo', sans-serif", fontWeight: "900", fontSize: "10px",
					letterSpacing: ".14em", textTransform: "uppercase", color: "#19d3e8", marginBottom: "4px",
				},
			}, kicker)
			: null,
		el("div", {
			style: {
				fontFamily: "'Anton', sans-serif", fontSize: "48px", lineHeight: ".82",
				textTransform: "uppercase", color: "#fff", textShadow: "0 3px 18px rgba(0,0,0,.45)",
			},
		}, title),
	);
	children.push(titleBlock);

	if (bar) {
		children.push(
			el("div", { style: { position: "absolute", left: "10px", right: "10px", bottom: "10px" } }, bar),
		);
	}

	return el("div", {
		style: {
			position: "relative", height, background: magTileBg(item), overflow: "hidden",
		},
	}, ...children.filter(Boolean));
}

// A small glass square button for hero corners (kebab/menu/etc).
export function magGlassSquare(content, { onClick, label } = {}) {
	return el("button", {
		type: "button",
		onclick: onClick,
		...(label ? { "aria-label": label } : {}),
		style: {
			height: "40px", width: "40px", border: `1px solid ${MAG.line}`,
			background: "rgba(10,10,14,.45)", borderRadius: "12px", color: "#fff",
			display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer",
			backdropFilter: "blur(10px)", webkitBackdropFilter: "blur(10px)",
		},
	}, content);
}

// ---- 1e. Poppy stat block --------------------------------------------------

// Solid poppy-coloured stat block. `darkText` for light fills (cyan).
export function magPoppyStat(n, label, color = MAG.lime, { darkText = false } = {}) {
	const fg = darkText ? "#06222a" : "#fff";
	return el(
		"div",
		{ style: { flex: "1", borderRadius: "11px", padding: "10px 9px", background: color, minWidth: "0" } },
		el("div", { style: { fontFamily: "'Anton', sans-serif", fontSize: "22px", color: fg, lineHeight: ".9" } }, String(n)),
		el("div", {
			style: {
				fontFamily: "'Archivo', sans-serif", fontWeight: "900", fontSize: "8px",
				letterSpacing: ".1em", textTransform: "uppercase",
				color: darkText ? "#06343f" : "rgba(255,255,255,.88)", marginTop: "3px",
			},
		}, label),
	);
}

// A glass stat strip: a single glass row of figures divided by hairlines, with
// an optional "Stats ▾" collapse toggle. `stats` = [{ value, label }]. Used in
// the collection header (stats demoted, not a dashboard).
export function magStatStrip(stats, { collapsible = false } = {}) {
	const row = el("div", {
		style: { display: "flex", alignItems: "stretch" },
	}, ...stats.flatMap((s, i) => {
		const cell = el(
			"div",
			{ style: { flex: "1", padding: "10px 12px", minWidth: "0" } },
			el("div", { style: { fontFamily: "'Anton', sans-serif", fontSize: "20px", color: "#fff", lineHeight: ".9" } }, String(s.value)),
			el("div", {
				style: {
					fontFamily: "'Archivo', sans-serif", fontWeight: "900", fontSize: "7.5px",
					letterSpacing: ".1em", textTransform: "uppercase", color: MAG.dim, marginTop: "3px",
				},
			}, s.label),
		);
		return i === 0
			? [cell]
			: [el("div", { style: { width: "1px", background: "rgba(255,255,255,.1)" } }), cell];
	}));

	const panel = magGlass(row, { style: { overflow: "hidden" } });
	if (!collapsible) return panel;

	const wrap = el("div", {});
	let open = true;
	const toggle = el("button", {
		type: "button",
		style: {
			fontFamily: "'Archivo', sans-serif", fontWeight: "900", fontSize: "9px",
			letterSpacing: ".1em", textTransform: "uppercase", color: MAG.dim,
			background: "transparent", border: "0", cursor: "pointer", padding: "6px 2px",
		},
		onclick: () => {
			open = !open;
			panel.style.display = open ? "" : "none";
			toggle.textContent = open ? "Stats ▴" : "Stats ▾";
		},
	}, "Stats ▴");
	wrap.append(toggle, panel);
	return wrap;
}

// ---- 1h. Episode / child row ----------------------------------------------

// A glass row: numbered cover thumb, kicker line, Anton title, trailing action.
// Used by TV/anime season→episode, book-series, and any ordered-child list.
// `owned` toggles the trailing control between "✓ <ownedLabel>" (lime outline)
// and "+ Add" (solid lime). Pass onAdd/onClick handlers.
export function magEpisodeRow(child, {
	number,
	kicker,
	owned = false,
	ownedLabel = "Have",
	addLabel = "+ Add",
	onAdd,
	onClick,
	dimmed = false,
	hideAction = false,
} = {}) {
	const thumb = magCover(child, {
		height: "62px", width: "46px", radius: "8px",
		children: number != null
			? [el("div", {
				style: {
					position: "absolute", top: "4px", left: "4px",
					fontFamily: "'Anton', sans-serif", fontSize: "13px", color: MAG.lime,
					background: "rgba(11,11,15,.7)", borderRadius: "5px", padding: "0 5px", lineHeight: "1.3",
				},
			}, String(number).padStart(2, "0"))]
			: [],
	});

	const body = el(
		"div",
		{ style: { flex: "1", minWidth: "0" } },
		kicker
			? el("div", {
				style: {
					fontFamily: "'Archivo', sans-serif", fontWeight: "800", fontSize: "9px",
					letterSpacing: ".06em", textTransform: "uppercase", color: MAG.dim, marginBottom: "3px",
				},
			}, kicker)
			: null,
		el("div", {
			style: {
				fontFamily: "'Anton', sans-serif", fontSize: "18px", lineHeight: ".95",
				textTransform: "uppercase", color: "#fff",
				overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
			},
		}, child.title ?? ""),
	);

	// Stop the action click from bubbling into the row's own onClick (navigate),
	// so a trailing button (e.g. "Seen") never doubles as a row tap.
	const handleAction = (e) => { e?.stopPropagation?.(); onAdd?.(e); };
	const action = hideAction
		? null
		: owned
			? magButton(`✓ ${ownedLabel}`, { variant: "owned", onClick: handleAction })
			: magButton(addLabel, { variant: "primary", onClick: handleAction });

	return magGlass(
		[thumb, body, action].filter(Boolean),
		{
			role: onClick ? "button" : undefined,
			onClick,
			style: {
				display: "flex", alignItems: "center", gap: "12px", padding: "10px 12px",
				...(dimmed ? { opacity: ".5" } : {}),
			},
		},
	);
}

// ---- 1i. Tabs / segmented control -----------------------------------------

// Glass pill toggle. `options` = [{ value, label }]. Calls onChange(value).
// magModeTabs (in-screen modes) and magSeg (settings) share styling.
export function magModeTabs(options, active, onChange) {
	const seg = (opt) => {
		const on = opt.value === active;
		return el("button", {
			type: "button",
			onclick: () => onChange?.(opt.value),
			style: {
				flex: "1", textAlign: "center", border: "0", cursor: "pointer",
				fontFamily: "'Archivo', sans-serif", fontWeight: on ? "900" : "800",
				fontSize: "11px", letterSpacing: ".06em", textTransform: "uppercase",
				color: on ? "#0b0b0f" : MAG.dim, background: on ? MAG.lime : "transparent",
				borderRadius: "999px", padding: "9px 0",
			},
		}, opt.label);
	};
	return el("div", {
		style: {
			display: "flex", background: MAG.bgGlass, border: `1px solid ${MAG.line}`,
			borderRadius: "999px", padding: "4px",
			backdropFilter: "blur(12px)", webkitBackdropFilter: "blur(12px)",
		},
	}, ...options.map(seg));
}
export const magSeg = magModeTabs;

// ---- 1j. Buttons -----------------------------------------------------------

// Actions. variant: primary (solid lime) | ghost (translucent) | danger | owned
// (lime outline). All ≥44px tall via padding. `block` makes it full width.
export function magButton(label, { variant = "primary", onClick, block = false, type = "button", style = {} } = {}) {
	const variants = {
		primary: { color: "#0b0b0f", background: MAG.lime, border: "0" },
		ghost: { color: "#fff", background: "rgba(255,255,255,.14)", border: `1px solid ${MAG.line}` },
		danger: { color: "#ff5a4a", background: "transparent", border: "1.5px solid rgba(255,90,74,.45)" },
		owned: { color: MAG.lime, background: "transparent", border: `1.5px solid ${MAG.lime}` },
	};
	const v = variants[variant] ?? variants.primary;
	return el("button", {
		type,
		onclick: onClick,
		style: {
			fontFamily: "'Archivo', sans-serif", fontWeight: "900", fontSize: "11px",
			letterSpacing: ".06em", textTransform: "uppercase", borderRadius: "999px",
			padding: "11px 16px", cursor: "pointer", lineHeight: "1",
			...(block ? { width: "100%", display: "block" } : { flex: "none" }),
			...v, ...style,
		},
	}, label);
}

// ---- 1k. States ------------------------------------------------------------

// Centered glass empty state: big Anton headline, subtext, optional primary CTA.
export function magEmpty({ title, subtitle, actionLabel, onAction } = {}) {
	return magGlass(
		[
			el("div", {
				style: { fontFamily: "'Anton', sans-serif", fontSize: "34px", textTransform: "uppercase", color: "#fff", lineHeight: ".9" },
			}, title ?? "Nothing here"),
			subtitle
				? el("div", {
					style: {
						fontFamily: "'Archivo', sans-serif", fontWeight: "600", fontSize: "13px",
						color: MAG.dim, marginTop: "8px", lineHeight: "1.4",
					},
				}, subtitle)
				: null,
			actionLabel && onAction ? magButton(actionLabel, { onClick: onAction, style: { marginTop: "16px" } }) : null,
		].filter(Boolean),
		{ style: { textAlign: "center", padding: "32px 22px", margin: "16px 0" } },
	);
}

// Skeleton placeholders with a shimmer (keyframe `mag-shimmer` lives in main.css).
// `shape: "gallery" | "list" | "hero"`.
function shimmerBox(style) {
	return el("div", {
		style: {
			borderRadius: "14px",
			background: "linear-gradient(100deg, rgba(255,255,255,.05) 30%, rgba(255,255,255,.12) 50%, rgba(255,255,255,.05) 70%)",
			backgroundSize: "200% 100%", animation: "mag-shimmer 1.3s ease-in-out infinite",
			...style,
		},
	});
}
export function magLoading({ shape = "gallery" } = {}) {
	if (shape === "hero") return shimmerBox({ height: "320px", borderRadius: "0" });
	if (shape === "list") {
		return el("div", { style: { display: "flex", flexDirection: "column", gap: "9px", padding: "10px 0" } },
			...Array.from({ length: 5 }, () => shimmerBox({ height: "80px" })));
	}
	// gallery: two columns of staggered tiles
	const col = (heights) => el("div", { style: { flex: "1", display: "flex", flexDirection: "column", gap: "9px" } },
		...heights.map((h) => shimmerBox({ height: `${h}px` })));
	return el("div", { style: { display: "flex", gap: "9px", padding: "10px 0" } },
		col([200, 130, 160]), col([150, 210, 120]));
}

// Glass error card with a red headline and a ghost Retry.
export function magError({ title = "Something went wrong", onRetry } = {}) {
	return magGlass(
		[
			el("div", {
				style: { fontFamily: "'Anton', sans-serif", fontSize: "26px", textTransform: "uppercase", color: "#ff5a4a" },
			}, title),
			onRetry ? magButton("Retry", { variant: "ghost", onClick: onRetry, style: { marginTop: "14px" } }) : null,
		].filter(Boolean),
		{ style: { textAlign: "center", padding: "28px 22px", margin: "16px 0" } },
	);
}

// ---- Page scaffolding ------------------------------------------------------

// A magazine screen masthead: a small count/eyebrow kicker over a huge Anton
// title. Used by list/add/stats/etc. screens that aren't a full feature hero.
export function magMasthead(title, { eyebrow, eyebrowColor = MAG.lime, size = "44px" } = {}) {
	return el(
		"div",
		{ style: { padding: "14px 4px 8px" } },
		eyebrow
			? el("div", {
				style: {
					fontFamily: "'Archivo', sans-serif", fontWeight: "900", fontSize: "10px",
					letterSpacing: ".12em", textTransform: "uppercase", color: eyebrowColor, marginBottom: "2px",
				},
			}, eyebrow)
			: null,
		el("div", {
			style: { fontFamily: "'Anton', sans-serif", fontSize: size, lineHeight: ".85", textTransform: "uppercase", color: "#fff" },
		}, title),
	);
}

// A screen header for non-hero screens (add / stats / vs / groups / account …):
// an optional glass back square on the left and the magMasthead. `onBack` wires
// the back control; omit it for top-level screens. Sits below the persistent
// top app bar.
export function magScreenHead(title, { eyebrow, eyebrowColor = MAG.lime, size = "42px", onBack } = {}) {
	const masthead = magMasthead(title, { eyebrow, eyebrowColor, size });
	if (!onBack) return masthead;
	return el(
		"div",
		{ style: { display: "flex", alignItems: "flex-start", gap: "10px", padding: "10px 0 0" } },
		el("div", { style: { paddingTop: "16px" } }, magGlassSquare("‹", { onClick: onBack, label: "Back" })),
		el("div", { style: { flex: "1", minWidth: "0" } }, masthead),
	);
}

// A poppy skewed section label used inside detail screens for field groups
// ("Played it?", "Got it?", …) — the same sticker, rotating accent per call.
export function magFieldLabel(text, color = MAG.lime, { textColor } = {}) {
	const dark = textColor ?? (isLight(color) ? "#0b0b0f" : "#fff");
	return el("div", {
		style: {
			fontFamily: "'Archivo', sans-serif", fontWeight: "900", fontSize: "10px",
			letterSpacing: ".06em", textTransform: "uppercase", color: dark, background: color,
			display: "inline-block", padding: "4px 9px", transform: "skewX(-7deg)", margin: "18px 0 8px",
		},
	}, text);
}

// Rough luminance check so kicker/label text stays legible on its fill.
function isLight(hex) {
	const m = /^#?([0-9a-f]{6})$/i.exec(hex);
	if (!m) return false;
	const n = parseInt(m[1], 16);
	const r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
	return (0.299 * r + 0.587 * g + 0.114 * b) > 150;
}
export { isLight };
